import { lazy, Suspense, useState } from "react";
import { Routes, Route } from "react-router-dom";
import LayoutDecider from "./layoutDecider";
import Loading from "./_loading";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import PrivateRoute from "./privateRoute";
import NoMatch from "./404";
import Unauthorized from "./403";
import FrontLayout from "./frontLayout";
import RoleBasedRedirect from "../../constants/RoleBasedRedirect";
import { ToastContainer } from "react-toastify";
import CommonGuard from "./CommonGuard";

const Login = lazy(() => import("../login"));
const ForgetPassword = lazy(() => import("../forgotPassword"));
const Dashboard = lazy(() => import("../admin/dashboard"));
const User = lazy(() => import("../admin/user"));
const UserList = lazy(() => import("../admin/userlist"));
const AuthorityUserList = lazy(() => import("../admin/authorityuser"));
const Profile = lazy(() => import("../profile"));
const ChangePassword = lazy(() => import("../changePassword"));
const PasswordReset = lazy(() => import("../passwordReset"));
const AuthorityList = lazy(() => import("../admin/authoritylist"));
const AuthorityUser = lazy(() => import("../admin/addauthority"));
const BasicDetail = lazy(() => import("../authority/basicDetail"));
const CaseManagerList = lazy(() => import("../authority/CaseManagerList"));
const Authoritylist = lazy(() => import("../authority/authorityUserList"));
const ActionDiaryList = lazy(() => import("../authority/ActionDiary/ActionDiaryList"));
const ActionItemText = lazy(() => import("../authority/ActionDiary/ActionItemText"));
const ContractorList = lazy(() => import("../admin/contractorList"));
const Contractor = lazy(() => import("../admin/contractor"));
const DashboardCaseManager = lazy(() => import("../authority/DashboardCaseManager"));
const ActionDiary = lazy(() => import("../authority/ActionDiary/ActionDiary"));
const ActionFileUpload = lazy(() => import("../authority/ActionDiary/ActionFileUpload"));
const VerifyAccount = lazy(() => import("../verifyAccount"));

const SuspenseWrap = ({ children }) => <Suspense fallback={<Loading />}>{children}</Suspense>;

const ReCaptchaWrap = ({ children }) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={process.env.REACT_APP_GOOGLERECAPCHA_KEY}
    scriptProps={{ defer: true, async: true }}>
    {children}
  </GoogleReCaptchaProvider>
);

const Router = () => {
  const [role] = useState("AUTHORITY");

  const Protected = ({ children, role }) => (
    <SuspenseWrap>
      <PrivateRoute role={role}>{children}</PrivateRoute>
    </SuspenseWrap>
  );

  return (
    <>
      <Routes>
        <Route path="/" element={<RoleBasedRedirect role={role} />} />
        <Route element={<FrontLayout />}>
          <Route
            path="/login"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <Login />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <ForgetPassword />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />
          <Route
            path="/verify-account"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <VerifyAccount />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />
          <Route
            path="/password-reset"
            element={
              <SuspenseWrap>
                <ReCaptchaWrap>
                  <PasswordReset />
                </ReCaptchaWrap>
              </SuspenseWrap>
            }
          />
        </Route>
        <Route element={<LayoutDecider />}>
          <Route
            path="/change-password"
            element={
              <Protected>
                <ChangePassword />
              </Protected>
            }
          />
          <Route
            path="/profile"
            element={
              <Protected>
                <Profile />
              </Protected>
            }
          />
          <Route
            path="/dashboard"
            element={
              <Protected role="ADMIN">
                <Dashboard />
              </Protected>
            }
          />

          <Route
            path="/dashboard"
            element={
              <Protected role="ADMIN">
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/admin"
            element={
              <Protected role="ADMIN">
                <UserList />
              </Protected>
            }
          />
          <Route
            path="/authority"
            element={
              <Protected role="ADMIN">
                <AuthorityList />
              </Protected>
            }
          />

          <Route
            path="/authority/contractor/list/:authorityId"
            element={
              <Protected role="ADMIN">
                <ContractorList />
              </Protected>
            }
          />

          <Route
            path="/:clienttype/:contractor/list/:type/:id?"
            element={
              <Protected>
                <Contractor />
              </Protected>
            }
          />
          <Route
            path="/:clienttype/user/:authorityId?"
            element={
              <Protected role="ADMIN">
                <AuthorityUserList />
              </Protected>
            }
          />
          <Route
            path="/:clienttype/:type/:id?"
            element={
              <Protected role="ADMIN">
                <User />
              </Protected>
            }
          />
          <Route
            path="/:clienttype/user/:type/:id?"
            element={
              <Protected>
                <AuthorityUser />
              </Protected>
            }
          />

          <Route
            path="/authority/dashboard"
            element={
              <Protected>
                <DashboardCaseManager />
              </Protected>
            }
          />

          <Route
            path="/authority/case/list"
            element={
              <Protected>
                <CaseManagerList />
              </Protected>
            }
          />

          <Route
            path="/case-manager/:type/:id?/:authorityId?"
            element={
              <Protected>
                <CommonGuard
                  allowedTypes={["add", "edit"]}
                  requireIdFor={["edit"]}
                  redirectTo="/case-manager/add">
                  <BasicDetail />
                </CommonGuard>
              </Protected>
            }
          />

          <Route
            path="/authority/user"
            element={
              <Protected role="AUTHORITY">
                <Authoritylist />
              </Protected>
            }
          />
          <Route
            path="/actionDiary/list/:id?"
            element={
              <Protected>
                <ActionDiaryList />
              </Protected>
            }
          />
          <Route
            path="/actionDiary/:type/:id?"
            element={
              <Protected>
                <ActionDiary />
              </Protected>
            }
          />
          <Route
            path="/actionItemText/:type/:id?"
            element={
              <Protected>
                <ActionItemText />
              </Protected>
            }
          />
          <Route
            path="/actionItemFile/:type/:id?"
            element={
              <Protected>
                <ActionFileUpload />
              </Protected>
            }
          />

          <Route path="*" element={<NoMatch />} />
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NoMatch />} />
      </Routes>

      <ToastContainer />
    </>
  );
};

export default Router;
